import React, {
    Fragment,
    memo,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { find, map } from 'underscore';
import {
    LendingHeader,
    LendingMarkdown,
    LendingProducts,
    LendingTypes,
} from 'app/modules/lending/components/sections';
import { LendingDivider } from './components/divider';
import { DeleteConfirm, LendingSection } from './components/section';
import { swapArrayLoc } from '../setting/components/footer';
import { LendingHeaderModal } from './components/modals/headerModal';
import { LendingMarkdownModal } from './components/modals/markdownModal';
import {
    LendingProductsModalContent,
    LendingSectionModal,
    LendingTypesModalContent,
} from '../setting/components/lending/modal';
import { LendingImgModal } from './components/modals/imgModal';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { isSectionChanged } from 'app/functions/isDeepEqual';
import { Context } from 'app/index';
import { LendingColumns } from './components/sections/columns';
import { Button } from 'antd';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { Code, Columns, Product, Text, Type } from 'app/shared/assets';
import { v4 as uuid } from 'uuid';
import s from './index.module.scss';
import { Confirm } from 'app/shared/confirm/confirm';
import { useDesktop } from 'app/hooks/useDesktop';
import { useClickOutside } from 'app/hooks/useClickOutside';

const Sections = {
    MARKDOWN: 'markdown',
    HEADER: 'header',
    TYPES: 'types',
    PRODUCTS: 'products',
    NULL: null,
};

const lendingTypesToComponents = {
    [Sections.MARKDOWN]: LendingMarkdown,
    [Sections.HEADER]: LendingHeader,
    [Sections.TYPES]: LendingTypes,
    [Sections.PRODUCTS]: LendingProducts,
    [Sections.NULL]: () => <></>,
};

function newSection(type) {
    return {
        id: uuid(),
        type,
    };
}

function copySection(section) {
    return {
        ...section,
        id: uuid(),
        size: 100,
    };
}

function modalInitialState() {
    return {
        visible: false,
        initialValues: null,
        onFinish: null,
    };
}

function typesModalInitialState() {
    return { ...modalInitialState(), initialValues: [] };
}

function productsModalInitialState() {
    return {
        ...modalInitialState(),
        initialValues: [],
        uuid: null,
    };
}

const getGenericKeys = ({ id, type, size, empty }) => ({
    id,
    type,
    size,
    empty,
});

export const mapSettingsMap = {
    [Sections.PRODUCTS]: (section) => ({
        ...getGenericKeys(section),
        data: map(section.data, (product) => product.id),
    }),
    [Sections.TYPES]: (section) => ({
        ...getGenericKeys(section),
        data: map(section.data, ({ id, img }) => ({ id, img: img || null })),
    }),
    [Sections.HEADER]: ({ data, ...rest }) => ({
        ...getGenericKeys(rest),
        data,
    }),
    [Sections.MARKDOWN]: ({ data, ...rest }) => ({
        ...getGenericKeys(rest),
        data,
    }),
    [Sections.NULL]: ({ data, ...rest }) => ({
        ...getGenericKeys(rest),
        data,
    }),
};

export const mapEqualMap = {
    [Sections.PRODUCTS]: (section) => ({
        ...section,
        products: map(section?.products, (product) => product.id),
    }),
    [Sections.TYPES]: (section) => ({
        ...section,
        types: map(section?.types, ({ id, img }) => ({ id, img })),
    }),
};

export const LendingSections = memo(
    ({ isAdmin, types, point, getProducts }) => {
        const {
            settings: {
                lendingSections: { snapshot, settings, setSettings: set },
            },
            showColumns: {
                showColumns: { showColumns, setShowColumns },
            },
        } = useContext(Context);

        const isDesktop = useDesktop();
        const [headerModal, setHeaderModal] = useState(modalInitialState());
        const [markdownModal, setMarkdownModal] = useState(modalInitialState());
        const [typesModal, setTypesModal] = useState(typesModalInitialState());
        const [productsModal, setProductsModal] = useState(
            productsModalInitialState()
        );
        const [imgModal, setImgModal] = useState(modalInitialState());
        const [isShowSectionsMenu, setIsShowSectionsMenu] = useState({
            0: false,
        });

        const [activeRowIndex, setActiveRowIndex] = useState(0);
        const [isMenu, setIsMenu] = useState([]);
        const [isSectionPlusOptions, setSectionPlusOptions] = useState([]);
        const [deleteDiscardModal, setDeleteDiscardModal] = useState(false);

        const showConfirmDelete = () => {
            setDeleteDiscardModal(true);
        };
        const hideConfirmDelete = () => {
            setDeleteDiscardModal(false);
        };

        const deleteRef = useRef();
        useClickOutside(deleteRef, hideConfirmDelete);
        useEffect(() => {
            if (settings.length > 0)
                setIsMenu((prev) => {
                    return [...prev, [false, false, false, false]];
                });
        }, [settings]);

        const propsMap = {
            [Sections.HEADER]: (indexRow, indexColumn, { data }) => {
                return {
                    header: data?.header,
                    description: data?.description,
                    img: data?.img,
                    alignDescription: data?.alignDescription,
                    alignHeader: data?.alignHeader,
                    alignImg: data?.alignImg,
                    classNameImg: data?.classNameImg,
                    colorHeader: data?.colorHeader,
                    colorDescription: data?.colorDescription,
                    classNameHeader: data?.classNameHeader,
                    sizeHeader: data?.sizeHeader,
                    sizeMobileHeader: data?.sizeMobileHeader,
                    sizeDescription: data?.sizeDescription,
                    sizeMobileDescription: data?.sizeMobileDescription,
                };
            },
            [Sections.MARKDOWN]: (indexRow, indexColumn, { data }) => {
                return {
                    markdown: data,
                };
            },
            [Sections.PRODUCTS]: (
                indexRow,
                indexColumn,
                { data, id, allProducts }
            ) => {
                return {
                    data,
                    point,
                    uuid: id,
                    setInitialData: setInitialData('products'),
                    setAllData: setInitialDataProducts('allProducts'),
                    allProducts: allProducts,
                    getProducts,
                };
            },
            [Sections.TYPES]: (indexRow, indexColumn, { id }) => ({
                point,
                uuid: id,
                allowEdit: isAdmin,
                setInitialData: setInitialData('types'),
                onAddImage: (id) => {
                    setImgModal((prevState) => ({
                        ...prevState,
                        visible: true,
                        onFinish: ({ img }) => {
                            setSettings((prevState) => {
                                const copy = JSON.parse(
                                    JSON.stringify(prevState)
                                );
                                copy[indexRow][indexColumn].data = prevState?.[
                                    indexRow
                                ]?.[indexColumn]?.data.map((el) => {
                                    delete el.children;
                                    return el.id === id ? { ...el, img } : el;
                                });

                                return copy;
                            });

                            setImgModal((prevState) => ({
                                ...prevState,
                                ...modalInitialState(),
                            }));
                        },
                    }));
                },
                onEdit: (id, img) =>
                    setImgModal((prevState) => ({
                        ...prevState,
                        visible: true,
                        initialValues: img,
                        onFinish: ({ img }) => {
                            setSettings((prevState) => {
                                const copy = JSON.parse(
                                    JSON.stringify(prevState)
                                );
                                copy[indexRow][indexColumn].data = prevState?.[
                                    indexRow
                                ]?.[indexColumn]?.data.map((el) =>
                                    el.id === id ? { ...el, img } : el
                                );
                                return copy;
                            });

                            setImgModal((prevState) => ({
                                ...prevState,
                                ...modalInitialState(),
                            }));
                        },
                    })),
                onDelete: (id) =>
                    setSettings((prevState) => {
                        const copy = JSON.parse(JSON.stringify(prevState));
                        copy[indexRow][indexColumn].data = prevState?.[
                            indexRow
                        ]?.[indexColumn]?.data.map((el) =>
                            el.id === id ? { ...el, img: '' } : el
                        );
                        return copy;
                    }),
            }),
        };
        const editsMap = {
            [Sections.HEADER]: (indexRow, indexColumn, { data }) => {
                setHeaderModal((prevState) => ({
                    ...prevState,
                    initialValues: {
                        header: data?.header,
                        description: data?.description,
                        img: data?.img,
                        alignDescription: data?.alignDescription,
                        alignHeader: data?.alignHeader,
                        alignImg: data?.alignImg,
                        classNameImg: data?.classNameImg,
                        colorHeader: data?.colorHeader,
                        colorDescription: data?.colorDescription,
                        classNameHeader: data?.classNameHeader,
                        sizeHeader: data?.sizeHeader,
                        sizeMobileHeader: data?.sizeMobileHeader,
                        sizeDescription: data?.sizeDescription,
                        sizeMobileDescription: data?.sizeMobileDescription,
                        id: data?.id,
                    },
                    visible: true,
                    onFinish: (values) => {
                        if (values.description || values.header || values.img) {
                            editSection(indexRow, indexColumn, {
                                data: values,
                            });
                        }
                        setHeaderModal((prevState) => ({
                            ...prevState,
                            ...modalInitialState(),
                        }));
                    },
                }));
            },
            [Sections.MARKDOWN]: (indexRow, indexColumn, { data }) => {
                setMarkdownModal((prevState) => ({
                    ...prevState,
                    initialValues: { markdown: data },
                    visible: true,
                    onFinish: (values) => {
                        editSection(indexRow, indexColumn, {
                            data: values.markdown,
                        });
                        setMarkdownModal((prevState) => ({
                            ...prevState,
                            ...modalInitialState(),
                        }));
                    },
                }));
            },
            [Sections.TYPES]: (indexRow, indexColumn, { data, id }) => {
                setTypesModal((prevState) => ({
                    ...prevState,
                    initialValues: data,
                    visible: true,
                    onFinish: (values) => {
                        editSection(indexRow, indexColumn, { data: values });
                        setTypesModal((prevState) => ({
                            ...prevState,
                            ...typesModalInitialState(),
                        }));
                    },
                }));
            },
            [Sections.PRODUCTS]: (indexRow, indexColumn, { data, id }) => {
                setProductsModal((prevState) => ({
                    ...prevState,
                    uuid: id,
                    initialValues: data,
                    visible: true,
                    onFinish: (products) => {
                        editSection(indexRow, indexColumn, {
                            data: products,
                        });
                        setProductsModal((prevState) => ({
                            ...prevState,
                            ...productsModalInitialState(),
                        }));
                    },
                }));
            },
        };

        function setInitialData() {
            return (id, data) => {
                set((prevState) => {
                    const newState = map(prevState.settings, (setting, ind) => {
                        return map(setting, (item) => {
                            if (item.id === id) {
                                return { ...item, data };
                            } else {
                                return item;
                            }
                        });
                    });

                    return {
                        settings: [...newState],
                        snapshot: [...newState],
                    };
                });
            };
        }
        function setInitialDataProducts(key) {
            return (id, data) => {
                set((prevState) => {
                    const newState = map(prevState.settings, (setting, ind) => {
                        return map(setting, (item) => {
                            if (item.id === id) {
                                return { ...item, [key]: data };
                            } else {
                                return item;
                            }
                        });
                    });

                    return {
                        settings: [...newState],
                        snapshot: [...newState],
                    };
                });
            };
        }
        function editSection(indexRow, indexColumn, properties) {
            setSettings((prevState) => {
                return map(prevState, (sections, index) => {
                    return map(sections, (section, _index) => {
                        if (_index === indexColumn && index === indexRow) {
                            return {
                                ...section,
                                ...(typeof properties === 'function'
                                    ? properties(section)
                                    : properties),
                            };
                        } else {
                            return section;
                        }
                    });
                });
            });
        }
        function insertInto(index = 0, section) {
            try {
                setSettings((prevState) => {
                    const copy = JSON.parse(JSON.stringify(prevState));
                    copy.splice(index, 0, section);
                    return copy;
                });
            } catch (e) {
                console.error(e);
            }
        }
        function addMarkdownSection(indexRow, indexColumn) {
            const section = newSection(Sections.MARKDOWN);

            setMarkdownModal((prevState) => ({
                ...prevState,
                visible: true,
                onFinish: ({ markdown }) => {
                    setActiveRowIndex(indexRow);
                    setSettings((prevState) => {
                        const copy = JSON.parse(JSON.stringify(prevState));

                        copy[indexRow][indexColumn] = {
                            ...prevState[indexRow][indexColumn],
                            ...section,
                            data: markdown,
                            empty: false,
                        };
                        return copy;
                    });
                    setMarkdownModal((prevState) => ({
                        ...prevState,
                        ...modalInitialState(),
                    }));
                },
            }));
        }
        function addMarkdown(indexRow, indexColumn) {
            const section = newSection(Sections.MARKDOWN);

            setMarkdownModal((prevState) => ({
                ...prevState,
                visible: true,

                onFinish: ({ markdown }) => {
                    setActiveRowIndex(indexRow);

                    setSettings((prevState) => {
                        const copy = JSON.parse(JSON.stringify(prevState));

                        copy.splice(indexRow, 0, [
                            {
                                ...section,
                                data: markdown,
                                size: 100,
                                empty: false,
                            },
                        ]);

                        return copy;
                    });

                    setMarkdownModal((prevState) => ({
                        ...prevState,
                        ...modalInitialState(),
                    }));
                },
            }));
        }
        function addHeaderSection(indexRow, indexColumn) {
            const section = newSection(Sections.HEADER);
            setHeaderModal((prevState) => ({
                ...prevState,
                visible: true,
                initialValues: {
                    colorHeader: '#333',
                    colorDescription: '#333',
                    sizeHeader: '32',
                    sizeMobileHeader: '32',
                    sizeDescription: '14',
                    sizeMobileDescription: '14',
                },
                onFinish: (values) => {
                    if (values.description || values.header || values.img) {
                        setActiveRowIndex(indexRow);
                        setSettings((prevState) => {
                            const copy = JSON.parse(JSON.stringify(prevState));

                            copy[indexRow][indexColumn] = {
                                ...prevState[indexRow][indexColumn],
                                ...section,
                                data: values,
                                empty: false,
                            };

                            return copy;
                        });
                    }
                    setHeaderModal((prevState) => ({
                        ...prevState,
                        ...modalInitialState(),
                    }));
                },
            }));
        }
        function addHeader(indexRow, indexColumn) {
            const section = newSection(Sections.HEADER);
            setHeaderModal((prevState) => {
                return {
                    ...prevState,
                    visible: true,
                    initialValues: {
                        colorHeader: '#333',
                        colorDescription: '#333',
                        sizeHeader: '32',
                        sizeMobileHeader: '32',
                        sizeDescription: '14',
                        sizeMobileDescription: '14',
                    },
                    onFinish: (values) => {
                        if (values.description || values.header || values.img) {
                            setActiveRowIndex(indexRow);

                            setSettings((prevState) => {
                                const copy = JSON.parse(
                                    JSON.stringify(prevState)
                                );

                                copy.splice(indexRow, 0, [
                                    {
                                        ...section,
                                        data: values,
                                        size: 100,
                                        empty: false,
                                    },
                                ]);

                                return copy;
                            });
                        }
                        setHeaderModal((prevState) => ({
                            ...prevState,
                            ...modalInitialState(),
                        }));
                    },
                };
            });
        }
        function addTypesSection(indexRow, indexColumn) {
            const section = newSection(Sections.TYPES);
            setTypesModal((prevState) => ({
                ...prevState,
                visible: true,
                onFinish: (values) => {
                    setActiveRowIndex(indexRow);
                    setSettings((prevState) => {
                        const copy = JSON.parse(JSON.stringify(prevState));

                        copy[indexRow][indexColumn] = {
                            ...prevState[indexRow][indexColumn],
                            ...section,

                            data: values,
                            empty: false,
                        };
                        return copy;
                    });
                    setTypesModal((prevState) => ({
                        ...prevState,
                        ...typesModalInitialState(),
                    }));
                },
            }));
        }
        function addTypes(indexRow, indexColumn) {
            const section = newSection(Sections.TYPES);
            setTypesModal((prevState) => ({
                ...prevState,
                visible: true,
                onFinish: (values) => {
                    setActiveRowIndex(indexRow);
                    if (values?.length) {
                        setSettings((prevState) => {
                            const copy = JSON.parse(JSON.stringify(prevState));

                            copy.splice(indexRow, 0, [
                                {
                                    ...section,
                                    data: values,
                                    size: 100,
                                    empty: false,
                                },
                            ]);

                            return copy;
                        });
                    }
                    setTypesModal((prevState) => ({
                        ...prevState,
                        ...typesModalInitialState(),
                    }));
                },
            }));
        }
        function addProductsSection(indexRow, indexColumn) {
            const section = newSection(Sections.PRODUCTS);
            setProductsModal((prevState) => ({
                ...prevState,
                visible: true,
                onFinish: (values) => {
                    setActiveRowIndex(indexRow);
                    setSettings((prevState) => {
                        const copy = JSON.parse(JSON.stringify(prevState));

                        copy[indexRow][indexColumn] = {
                            ...prevState[indexRow][indexColumn],
                            ...section,
                            data: values,
                            empty: false,
                            allProducts: values,
                        };
                        return copy;
                    });

                    setProductsModal((prevState) => ({
                        ...prevState,
                        ...productsModalInitialState(),
                    }));
                },
            }));
        }
        function addProducts(indexRow, indexColumn) {
            const section = newSection(Sections.PRODUCTS);
            setProductsModal((prevState) => ({
                ...prevState,
                visible: true,
                onFinish: (values) => {
                    setActiveRowIndex(indexRow);
                    if (values?.length) {
                        setSettings((prevState) => {
                            const copy = JSON.parse(JSON.stringify(prevState));

                            copy.splice(indexRow, 0, [
                                {
                                    ...section,
                                    data: values,
                                    size: 100,
                                    empty: false,
                                    allProducts: values,
                                },
                            ]);

                            return copy;
                        });
                    }

                    setProductsModal((prevState) => ({
                        ...prevState,
                        ...productsModalInitialState(),
                    }));
                },
            }));
        }

        function addColumns(rowIndex) {
            setActiveRowIndex(rowIndex);
            setShowColumns(true);
        }

        function onDiscard(id) {
            setSettings((prevState) =>
                map(prevState, (row) => {
                    return map(row, (section) => {
                        if (section.id !== id) return section;
                        const snapshotSection = find(
                            snapshot,
                            (item) => item.id === id
                        );
                        if (snapshotSection) return snapshotSection;
                        return section;
                    });
                })
            );
        }
        function onDelete(rowIndex, columnIndex) {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                if (copy[rowIndex].length === 1) {
                    copy.splice(rowIndex, 1);
                    return copy;
                }
                copy[rowIndex].splice(columnIndex, 1, {
                    size: copy[rowIndex][columnIndex].size,
                    empty: true,
                    id: copy[rowIndex][columnIndex]?.id,
                    type: copy[rowIndex][columnIndex]?.type,
                });
                return copy;
            });
        }
        function onDeleteEmpty(rowIndex, columnIndex) {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));

                copy.splice(rowIndex, 1);

                return copy;
            });
        }
        function onCopy(indexRow, indexColumn) {
            insertInto(indexRow + 1, [
                copySection(settings[indexRow][indexColumn]),
            ]);
        }
        function onUp(indexRow) {
            if (indexRow === 0) return null;
            return () =>
                setSettings((prevState) =>
                    swapArrayLoc(prevState, indexRow, indexRow - 1)
                );
        }
        function onDown(indexRow) {
            if (indexRow === settings?.length - 1) return null;
            return () =>
                setSettings((prevState) =>
                    swapArrayLoc(prevState, indexRow, indexRow + 1)
                );
        }

        function onCancel(dispatch, initialState = modalInitialState) {
            dispatch((prevState) => ({ ...prevState, ...initialState() }));
        }

        function setSettings(setter) {
            set((prevState) => ({
                ...prevState,
                settings: setter(prevState.settings),
            }));
        }

        function inTwoBlocks() {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                copy.splice(activeRowIndex, 0, [
                    { id: uuid(), type: null, size: 50, empty: true },
                    { id: uuid(), type: null, size: 50, empty: true },
                ]);

                return copy;
            });

            setShowColumns(false);
        }

        function threeBlocks() {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                copy.splice(activeRowIndex, 0, [
                    { id: uuid(), type: null, size: 33.33333333, empty: true },
                    { id: uuid(), type: null, size: 33.33333333, empty: true },
                    { id: uuid(), type: null, size: 33.33333333, empty: true },
                ]);
                return copy;
            });

            setShowColumns(false);
        }
        function fourBlocks() {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                copy.splice(activeRowIndex, 0, [
                    { id: uuid(), type: null, size: 25, empty: true },
                    { id: uuid(), type: null, size: 25, empty: true },
                    { id: uuid(), type: null, size: 25, empty: true },
                    { id: uuid(), type: null, size: 25, empty: true },
                ]);
                return copy;
            });

            setShowColumns(false);
        }
        function twoBlocksNotEqual() {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                copy.splice(activeRowIndex, 0, [
                    { id: uuid(), type: null, size: 25, empty: true },
                    { id: uuid(), type: null, size: 75, empty: true },
                ]);
                return copy;
            });

            setShowColumns(false);
        }
        function twoBlocksNotEqualReverse() {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                copy.splice(activeRowIndex, 0, [
                    { id: uuid(), type: null, size: 75, empty: true },
                    { id: uuid(), type: null, size: 25, empty: true },
                ]);
                return copy;
            });

            setShowColumns(false);
        }
        function threeBlocksNotEqual() {
            setSettings((prevState) => {
                const copy = JSON.parse(JSON.stringify(prevState));
                copy.splice(activeRowIndex, 0, [
                    { id: uuid(), type: null, size: 25, empty: true },
                    { id: uuid(), type: null, size: 50, empty: true },
                    { id: uuid(), type: null, size: 25, empty: true },
                ]);
                return copy;
            });

            setShowColumns(false);
        }
        const [parent] = useAutoAnimate();

        return (
            <>
                <LendingSectionModal
                    visible={typesModal.visible}
                    onCancel={() =>
                        onCancel(setTypesModal, typesModalInitialState)
                    }
                    title={`Добавить категории`}
                    uniqueClass="lending-types-modal-uniqueClass"
                >
                    <LendingTypesModalContent
                        checkStrictly
                        tree={types}
                        initialItems={typesModal.initialValues}
                        onSubmit={typesModal.onFinish}
                    />
                </LendingSectionModal>
                <LendingSectionModal
                    visible={productsModal.visible}
                    onCancel={() =>
                        onCancel(setProductsModal, productsModalInitialState)
                    }
                    title={`Добавить товары`}
                    uniqueClass="lending-products-modal-uniqueClass"
                >
                    <LendingProductsModalContent
                        initialItems={productsModal.initialValues}
                        onSubmit={productsModal.onFinish}
                        uuid={productsModal.uuid}
                    />
                </LendingSectionModal>
                <LendingImgModal
                    visible={imgModal.visible}
                    onCancel={() => onCancel(setImgModal)}
                    onFinish={imgModal.onFinish}
                    initialValue={imgModal.initialValues}
                />
                <LendingHeaderModal
                    visible={headerModal.visible}
                    initialValues={headerModal.initialValues}
                    onCancel={() => onCancel(setHeaderModal)}
                    onFinish={headerModal.onFinish}
                />
                <LendingMarkdownModal
                    visible={markdownModal.visible}
                    initialValues={markdownModal.initialValues}
                    onCancel={() => onCancel(setMarkdownModal)}
                    onFinish={markdownModal.onFinish}
                />
                <div ref={parent} className={'lending-sections'}>
                    {isAdmin && settings.length === 0 ? (
                        <LendingDivider
                            onHeader={() => addHeader(0, 0)}
                            onMarkdown={() => addMarkdown(0, 0)}
                            onProducts={() => addProducts(0, 0)}
                            onTypes={() => addTypes(0, 0)}
                            onColumns={() => addColumns(0)}
                        />
                    ) : null}

                    <>
                        {map(injectHeaderId(settings), (row, index) => {
                            return (
                                <React.Fragment key={`${index}${row?.[0]?.id}`}>
                                    {isAdmin &&
                                        showColumns &&
                                        activeRowIndex === index && (
                                            <LendingColumns
                                                isAdmin={isAdmin}
                                                show={showColumns}
                                                setShowColumns={setShowColumns}
                                                inTwoBlocks={() =>
                                                    inTwoBlocks()
                                                }
                                                threeBlocks={() =>
                                                    threeBlocks()
                                                }
                                                fourBlocks={() => fourBlocks()}
                                                twoBlocksNotEqual={() =>
                                                    twoBlocksNotEqual()
                                                }
                                                twoBlocksNotEqualReverse={() =>
                                                    twoBlocksNotEqualReverse()
                                                }
                                                threeBlocksNotEqual={() =>
                                                    threeBlocksNotEqual()
                                                }
                                            />
                                        )}
                                    <div
                                        onMouseEnter={() =>
                                            setIsShowSectionsMenu((prev) => {
                                                return {
                                                    ...prev,
                                                    [index]: true,
                                                };
                                            })
                                        }
                                        onMouseLeave={() =>
                                            setIsShowSectionsMenu((prev) => {
                                                return {
                                                    ...prev,
                                                    [index]: false,
                                                };
                                            })
                                        }
                                        className={s.lending_sections_wrap}
                                        key={index}
                                    >
                                        {isShowSectionsMenu[index] &&
                                        isAdmin ? (
                                            <div className={s.sections_menu}>
                                                <Button
                                                    shape={'circle'}
                                                    icon={<PlusOutlined />}
                                                    onClick={() =>
                                                        setSectionPlusOptions(
                                                            (prev) => {
                                                                if (
                                                                    !prev.length
                                                                ) {
                                                                    for (
                                                                        let i = 0;
                                                                        i <=
                                                                        index;
                                                                        i++
                                                                    ) {
                                                                        prev[
                                                                            i
                                                                        ] = false;
                                                                    }
                                                                }
                                                                const copy =
                                                                    JSON.parse(
                                                                        JSON.stringify(
                                                                            prev
                                                                        )
                                                                    );

                                                                copy.splice(
                                                                    index,
                                                                    1,
                                                                    !copy[index]
                                                                );

                                                                return copy;
                                                            }
                                                        )
                                                    }
                                                />
                                                {isSectionPlusOptions?.[
                                                    index
                                                ] && (
                                                    <div className={s.dropdown}>
                                                        <div
                                                            onClick={() => {
                                                                addHeader(
                                                                    index,
                                                                    0
                                                                );
                                                                setSectionPlusOptions(
                                                                    (prev) => {
                                                                        if (
                                                                            !prev.length
                                                                        ) {
                                                                            for (
                                                                                let i = 0;
                                                                                i <=
                                                                                index;
                                                                                i++
                                                                            ) {
                                                                                prev[
                                                                                    i
                                                                                ] = false;
                                                                            }
                                                                        }
                                                                        const copy =
                                                                            JSON.parse(
                                                                                JSON.stringify(
                                                                                    prev
                                                                                )
                                                                            );

                                                                        copy.splice(
                                                                            index,
                                                                            1,
                                                                            false
                                                                        );

                                                                        return copy;
                                                                    }
                                                                );
                                                            }}
                                                            className={
                                                                s.dropdown_row
                                                            }
                                                        >
                                                            <Text />
                                                            <div
                                                                className={
                                                                    s.dropdown_item
                                                                }
                                                            >
                                                                Заголовок
                                                            </div>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                addTypes(
                                                                    index,
                                                                    0
                                                                );
                                                                setSectionPlusOptions(
                                                                    (prev) => {
                                                                        if (
                                                                            !prev.length
                                                                        ) {
                                                                            for (
                                                                                let i = 0;
                                                                                i <=
                                                                                index;
                                                                                i++
                                                                            ) {
                                                                                prev[
                                                                                    i
                                                                                ] = false;
                                                                            }
                                                                        }
                                                                        const copy =
                                                                            JSON.parse(
                                                                                JSON.stringify(
                                                                                    prev
                                                                                )
                                                                            );

                                                                        copy.splice(
                                                                            index,
                                                                            1,
                                                                            false
                                                                        );

                                                                        return copy;
                                                                    }
                                                                );
                                                            }}
                                                            className={
                                                                s.dropdown_row
                                                            }
                                                        >
                                                            <Type />
                                                            <div
                                                                className={
                                                                    s.dropdown_item
                                                                }
                                                            >
                                                                Категории
                                                            </div>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                addColumns(
                                                                    index
                                                                );
                                                                setSectionPlusOptions(
                                                                    (prev) => {
                                                                        if (
                                                                            !prev.length
                                                                        ) {
                                                                            for (
                                                                                let i = 0;
                                                                                i <=
                                                                                index;
                                                                                i++
                                                                            ) {
                                                                                prev[
                                                                                    i
                                                                                ] = false;
                                                                            }
                                                                        }
                                                                        const copy =
                                                                            JSON.parse(
                                                                                JSON.stringify(
                                                                                    prev
                                                                                )
                                                                            );

                                                                        copy.splice(
                                                                            index,
                                                                            1,
                                                                            false
                                                                        );

                                                                        return copy;
                                                                    }
                                                                );
                                                            }}
                                                            className={
                                                                s.dropdown_row
                                                            }
                                                        >
                                                            <Columns />
                                                            <div
                                                                className={
                                                                    s.dropdown_item
                                                                }
                                                            >
                                                                Колонки
                                                            </div>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                addProducts(
                                                                    index,
                                                                    0
                                                                );
                                                                setSectionPlusOptions(
                                                                    (prev) => {
                                                                        if (
                                                                            !prev.length
                                                                        ) {
                                                                            for (
                                                                                let i = 0;
                                                                                i <=
                                                                                index;
                                                                                i++
                                                                            ) {
                                                                                prev[
                                                                                    i
                                                                                ] = false;
                                                                            }
                                                                        }
                                                                        const copy =
                                                                            JSON.parse(
                                                                                JSON.stringify(
                                                                                    prev
                                                                                )
                                                                            );

                                                                        copy.splice(
                                                                            index,
                                                                            1,
                                                                            false
                                                                        );

                                                                        return copy;
                                                                    }
                                                                );
                                                            }}
                                                            className={
                                                                s.dropdown_row
                                                            }
                                                        >
                                                            <Product />
                                                            <div
                                                                className={
                                                                    s.dropdown_item
                                                                }
                                                            >
                                                                Товары
                                                            </div>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                addMarkdown(
                                                                    index,
                                                                    0
                                                                );
                                                                setSectionPlusOptions(
                                                                    (prev) => {
                                                                        if (
                                                                            !prev.length
                                                                        ) {
                                                                            for (
                                                                                let i = 0;
                                                                                i <=
                                                                                index;
                                                                                i++
                                                                            ) {
                                                                                prev[
                                                                                    i
                                                                                ] = false;
                                                                            }
                                                                        }
                                                                        const copy =
                                                                            JSON.parse(
                                                                                JSON.stringify(
                                                                                    prev
                                                                                )
                                                                            );

                                                                        copy.splice(
                                                                            index,
                                                                            1,
                                                                            false
                                                                        );

                                                                        return copy;
                                                                    }
                                                                );
                                                            }}
                                                            className={
                                                                s.dropdown_row
                                                            }
                                                        >
                                                            <Code />
                                                            <div
                                                                className={
                                                                    s.dropdown_item
                                                                }
                                                            >
                                                                HTML-код
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {isDesktop ? (
                                                    <div>
                                                        <Confirm
                                                            onConfirm={() =>
                                                                onDeleteEmpty(
                                                                    index
                                                                )
                                                            }
                                                            description={
                                                                'Вы уверены, что хотите удалить раздел?'
                                                            }
                                                        >
                                                            <Button
                                                                title={
                                                                    'Удалить'
                                                                }
                                                                icon={
                                                                    <DeleteOutlined />
                                                                }
                                                            />
                                                        </Confirm>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div
                                                            className={`${s.mobile_menu} ${s.discard_wrap}`}
                                                            ref={deleteRef}
                                                        >
                                                            {deleteDiscardModal && (
                                                                <div
                                                                    className={
                                                                        s.discard_content
                                                                    }
                                                                >
                                                                    <DeleteConfirm
                                                                        onDelete={() =>
                                                                            onDeleteEmpty(
                                                                                index
                                                                            )
                                                                        }
                                                                        hideConfirmDelete={
                                                                            hideConfirmDelete
                                                                        }
                                                                    ></DeleteConfirm>
                                                                </div>
                                                            )}
                                                            <Button
                                                                title={
                                                                    'Удалить'
                                                                }
                                                                icon={
                                                                    <DeleteOutlined />
                                                                }
                                                                onClick={
                                                                    showConfirmDelete
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {onUp(index) ? (
                                                    <Button
                                                        onClick={onUp(index)}
                                                        shape="circle"
                                                        icon={
                                                            <ArrowUpOutlined />
                                                        }
                                                    />
                                                ) : null}
                                                {onDown(index) ? (
                                                    <Button
                                                        onClick={onDown(index)}
                                                        shape="circle"
                                                        icon={
                                                            <ArrowDownOutlined />
                                                        }
                                                    />
                                                ) : null}
                                            </div>
                                        ) : null}
                                        {row.map((section, ind) => {
                                            const { type, id, size, ...rest } =
                                                section;

                                            const Section =
                                                lendingTypesToComponents[type];

                                            let snapshotIndex = -1;
                                            const sectionSnapshot = find(
                                                snapshot,
                                                (item, index) => {
                                                    const equal =
                                                        item.id === section.id;
                                                    if (equal) {
                                                        snapshotIndex = index;
                                                    }
                                                    return equal;
                                                }
                                            );
                                            if (!Section) return null;
                                            return (
                                                <Fragment key={id}>
                                                    <LendingSection
                                                        id={section?.data?.id}
                                                        dataId={section?.dataID}
                                                        isAdmin={isAdmin}
                                                        changed={
                                                            isAdmin &&
                                                            isSectionChanged(
                                                                section,
                                                                sectionSnapshot,
                                                                index,
                                                                snapshotIndex
                                                            )
                                                        }
                                                        isEmpty={section?.empty}
                                                        size={size}
                                                        show
                                                        onDeleteEmpty={() =>
                                                            onDeleteEmpty(
                                                                index,
                                                                ind
                                                            )
                                                        }
                                                        actions={{
                                                            onEdit: () =>
                                                                editsMap[type](
                                                                    index,
                                                                    ind,
                                                                    section
                                                                ),
                                                            onDiscard: () =>
                                                                onDiscard(id),
                                                            onDelete: () =>
                                                                onDelete(
                                                                    index,
                                                                    ind
                                                                ),
                                                            onCopy: () =>
                                                                onCopy(
                                                                    index,
                                                                    ind
                                                                ),
                                                            onUp: onUp(index),
                                                            onDown: onDown(
                                                                index
                                                            ),
                                                        }}
                                                    >
                                                        {section?.empty &&
                                                            isAdmin && (
                                                                <div
                                                                    style={{
                                                                        display:
                                                                            'flex',
                                                                        justifyContent:
                                                                            'center',
                                                                        width: '50%',
                                                                    }}
                                                                    className="section-plus"
                                                                >
                                                                    <Button
                                                                        shape={
                                                                            'circle'
                                                                        }
                                                                        icon={
                                                                            <PlusOutlined />
                                                                        }
                                                                        onClick={() =>
                                                                            setIsMenu(
                                                                                (
                                                                                    prev
                                                                                ) => {
                                                                                    const copy =
                                                                                        JSON.parse(
                                                                                            JSON.stringify(
                                                                                                prev
                                                                                            )
                                                                                        );
                                                                                    const copyRow =
                                                                                        JSON.parse(
                                                                                            JSON.stringify(
                                                                                                copy[
                                                                                                    index
                                                                                                ]
                                                                                            )
                                                                                        );

                                                                                    copyRow.splice(
                                                                                        ind,
                                                                                        1,
                                                                                        !copyRow[
                                                                                            ind
                                                                                        ]
                                                                                    );
                                                                                    copy.splice(
                                                                                        index,
                                                                                        1,
                                                                                        copyRow
                                                                                    );

                                                                                    return copy;
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                    {isMenu?.[
                                                                        index
                                                                    ]?.[
                                                                        ind
                                                                    ] && (
                                                                        <div
                                                                            className={
                                                                                s.dropdown
                                                                            }
                                                                        >
                                                                            <div
                                                                                onClick={() => {
                                                                                    addHeaderSection(
                                                                                        index,
                                                                                        ind
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    s.dropdown_row
                                                                                }
                                                                            >
                                                                                <Text />
                                                                                <div
                                                                                    className={
                                                                                        s.dropdown_item
                                                                                    }
                                                                                >
                                                                                    Заголовок
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                onClick={() => {
                                                                                    addTypesSection(
                                                                                        index,
                                                                                        ind
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    s.dropdown_row
                                                                                }
                                                                            >
                                                                                <Type />
                                                                                <div
                                                                                    className={
                                                                                        s.dropdown_item
                                                                                    }
                                                                                >
                                                                                    Категории
                                                                                </div>
                                                                            </div>

                                                                            <div
                                                                                onClick={() => {
                                                                                    addProductsSection(
                                                                                        index,
                                                                                        ind
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    s.dropdown_row
                                                                                }
                                                                            >
                                                                                <Product />
                                                                                <div
                                                                                    className={
                                                                                        s.dropdown_item
                                                                                    }
                                                                                >
                                                                                    Товары
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                onClick={() => {
                                                                                    addMarkdownSection(
                                                                                        index,
                                                                                        ind
                                                                                    );
                                                                                }}
                                                                                className={
                                                                                    s.dropdown_row
                                                                                }
                                                                            >
                                                                                <Code />
                                                                                <div
                                                                                    className={
                                                                                        s.dropdown_item
                                                                                    }
                                                                                >
                                                                                    HTML-код
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                        <Section
                                                            isAdmin={isAdmin}
                                                            isAdded={find(
                                                                snapshot[index],
                                                                (item) =>
                                                                    item.id ===
                                                                    id
                                                            )}
                                                            uuid={id}
                                                            {...rest}
                                                            {...propsMap?.[
                                                                type
                                                            ]?.(
                                                                index,
                                                                ind,
                                                                section
                                                            )}
                                                        />
                                                    </LendingSection>
                                                </Fragment>
                                            );
                                        })}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        {isAdmin &&
                            showColumns &&
                            (activeRowIndex === settings.length ||
                                !activeRowIndex) && (
                                <LendingColumns
                                    isAdmin={isAdmin}
                                    show={showColumns}
                                    setShowColumns={setShowColumns}
                                    inTwoBlocks={() => inTwoBlocks()}
                                    threeBlocks={() => threeBlocks()}
                                    fourBlocks={() => fourBlocks()}
                                    twoBlocksNotEqual={() =>
                                        twoBlocksNotEqual()
                                    }
                                    twoBlocksNotEqualReverse={() =>
                                        twoBlocksNotEqualReverse()
                                    }
                                    threeBlocksNotEqual={() =>
                                        threeBlocksNotEqual()
                                    }
                                />
                            )}
                        {isAdmin && settings.length !== 0 ? (
                            <LendingDivider
                                onHeader={() => addHeader(settings.length, 0)}
                                onMarkdown={() =>
                                    addMarkdown(settings.length, 0)
                                }
                                onProducts={() =>
                                    addProducts(settings.length, 0)
                                }
                                onTypes={() => addTypes(settings.length, 0)}
                                onColumns={() => {
                                    addColumns(settings.length);
                                    setActiveRowIndex(settings.length);
                                }}
                            />
                        ) : null}
                    </>
                </div>
            </>
        );
    }
);

function injectHeaderId(row) {
    let currentHeader = null;
    return map(row, (sections, index, array) => {
        return map(sections, (section) => {
            if (section?.data?.id) currentHeader = index;
            const id =
                section?.data?.id ||
                find(array?.[currentHeader], (item) => item?.data?.id)?.data
                    ?.id;
            if (id) {
                return {
                    ...section,
                    dataID: id,
                };
            }
            return section;
        });
    });
}
